import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { OpenStatusDecision, StatusDecisionTriggerFacts } from "./types";
import { StatusDecisionDirection, StatusDecisionReason } from "./types";

/** i18n key suffixes per reason — title + body live under `team.status.decision.reason.<reason>.*`. */
const REASON_KEY: Record<StatusDecisionReason, string> = {
	[StatusDecisionReason.TermWithActivity]: "term_with_activity",
	[StatusDecisionReason.IdleNoTermWasActive]: "idle_no_term_was_active",
	[StatusDecisionReason.IdleNoTerm]: "idle_no_term",
	[StatusDecisionReason.InactiveGotActual]: "inactive_got_actual",
	[StatusDecisionReason.Rehired]: "rehired",
	[StatusDecisionReason.JobNotEnabled]: "job_not_enabled",
};

export function getReasonTitle(t: TFunction, reason: StatusDecisionReason): string {
	return t(`team.status.decision.reason.${REASON_KEY[reason]}.title`);
}

export function getReasonBody(t: TFunction, reason: StatusDecisionReason): string {
	return t(`team.status.decision.reason.${REASON_KEY[reason]}.body`);
}

function formatDecisionDate(iso: string): string {
	return DateTime.fromISO(iso).toFormat("MMM d, yyyy");
}

export type EvidenceLine = { label: string; value: string };

/** i18n keys for each evidence line, under `team.status.decision.evidence.*`. */
const EVIDENCE_KEY = {
	terminationDate: "team.status.decision.evidence.termination_date",
	rehireDate: "team.status.decision.evidence.rehire_date",
	resolvedJob: "team.status.decision.evidence.resolved_job",
	lastActualDate: "team.status.decision.evidence.last_actual_date",
	lastScheduledDate: "team.status.decision.evidence.last_scheduled_date",
	futureScheduled: "team.status.decision.evidence.future_scheduled",
} as const;

/**
 * Which evidence lines to render per reason, in order. Only lines whose
 * underlying triggerFacts value is present (non-null date / meaningful flag)
 * are returned — null dates are skipped.
 */
export function getEvidenceLines(t: TFunction, decision: OpenStatusDecision): EvidenceLine[] {
	const facts: StatusDecisionTriggerFacts = decision.triggerFacts;
	const lines: EvidenceLine[] = [];

	const pushDate = (labelKey: string, iso: string | null) => {
		if (iso) {
			lines.push({ label: t(labelKey), value: formatDecisionDate(iso) });
		}
	};

	switch (decision.reason) {
		case StatusDecisionReason.TermWithActivity:
			pushDate(EVIDENCE_KEY.terminationDate, facts.terminationDate);
			if (facts.hasFutureScheduled !== null) {
				lines.push({
					label: t(EVIDENCE_KEY.futureScheduled),
					value: facts.hasFutureScheduled ? t("yes") : t("no"),
				});
			}
			pushDate(EVIDENCE_KEY.lastScheduledDate, facts.lastScheduledDate);
			pushDate(EVIDENCE_KEY.lastActualDate, facts.lastActualDate);
			break;
		case StatusDecisionReason.IdleNoTermWasActive:
			pushDate(EVIDENCE_KEY.lastScheduledDate, facts.lastScheduledDate);
			pushDate(EVIDENCE_KEY.lastActualDate, facts.lastActualDate);
			break;
		case StatusDecisionReason.IdleNoTerm:
			pushDate(EVIDENCE_KEY.lastActualDate, facts.lastActualDate);
			pushDate(EVIDENCE_KEY.lastScheduledDate, facts.lastScheduledDate);
			break;
		case StatusDecisionReason.InactiveGotActual:
			pushDate(EVIDENCE_KEY.lastActualDate, facts.lastActualDate);
			break;
		case StatusDecisionReason.Rehired:
			pushDate(EVIDENCE_KEY.rehireDate, facts.rehireDate);
			break;
		case StatusDecisionReason.JobNotEnabled:
			// The evidence here isn't a date — it's which job the role string landed on, which is the
			// thing the scheduler has to either enable or change.
			if (facts.jobName) {
				lines.push({ label: t(EVIDENCE_KEY.resolvedJob), value: facts.jobName });
			}
			break;
	}

	return lines;
}

export function getPrimaryActionLabel(t: TFunction, direction: StatusDecisionDirection): string {
	return direction === StatusDecisionDirection.Deactivate ? t("team.status.decision.action.deactivate") : t("team.status.decision.action.reactivate");
}

export function getSecondaryActionLabel(t: TFunction, direction: StatusDecisionDirection): string {
	return direction === StatusDecisionDirection.Deactivate ? t("team.status.decision.action.keep_active") : t("team.status.decision.action.keep_inactive");
}
